import apiClient from '../axios.js';

const BASE = '/line/message-templates';

export const lineMsgTemplateService = {
  async list(params = {}) {
    return apiClient.get(BASE, { params });
  },

  async getById(id) {
    return apiClient.get(`${BASE}/${id}`);
  },

  async create(data) {
    return apiClient.post(BASE, data);
  },

  async update(id, data) {
    return apiClient.put(`${BASE}/${id}`, data);
  },

  async remove(id) {
    return apiClient.delete(`${BASE}/${id}`);
  },

  /** 複製範本（後端會一併複製內容引用的 S3 檔案） */
  async duplicate(id) {
    return apiClient.post(`${BASE}/${id}/duplicate`);
  },

  async compileFlex(presetKey, fields) {
    return apiClient.post(`${BASE}/compile-flex`, { presetKey, fields });
  },

  async getPresets() {
    return apiClient.get(`${BASE}/presets`);
  },

  /**
   * 上傳圖片：取得預簽名 URL 後直傳 S3
   * （不走 /upload，因為 API Gateway → Lambda 的 payload 上限只有 6MB，
   *   binary 經 base64 後實際更少，撐不到 LINE 允許的 10MB）
   * @param {File} file - JPEG 或 PNG
   * @returns {Promise<{success: boolean, data: {fileUrl: string, objectKey: string}}>}
   *   回傳格式與舊的 /upload 一致，供 ImageUploader 的 uploadFn 使用
   */
  async uploadImage(file) {
    const { fileUrl, objectKey } = await uploadToS3Direct(file);
    return { success: true, data: { fileUrl, objectKey } };
  },

  /**
   * 上傳影片：取得預簽名 URL 後直傳 S3
   * @param {File} file - MP4 影片檔
   * @param {Function} [onProgress] - 進度回呼（0-100）
   * @returns {Promise<{fileUrl: string, objectKey: string}>}
   */
  async uploadVideo(file, onProgress) {
    return uploadToS3Direct(file, onProgress, '影片');
  }
};

/**
 * 取得預簽名 PUT URL 並直傳 S3
 * @param {File} file
 * @param {Function} [onProgress] - 進度回呼（0-100）
 * @param {string} [label] - 錯誤訊息用的名稱
 * @returns {Promise<{fileUrl: string, objectKey: string}>}
 */
async function uploadToS3Direct(file, onProgress, label = '圖片') {
  const urlRes = await apiClient.post(`${BASE}/upload-url`, {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size
  });
  const { presignedUrl, objectKey, fileUrl } = urlRes?.data || {};
  if (!presignedUrl || !fileUrl) {
    throw new Error(urlRes?.message || '無法取得上傳 URL');
  }

  // 使用 XMLHttpRequest 直傳 S3 以取得真實上傳進度
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`${label}上傳失敗（HTTP ${xhr.status}）`));
      }
    };
    xhr.onerror = () => reject(new Error(`${label}上傳失敗：網路連線錯誤`));
    xhr.onabort = () => reject(new Error(`${label}上傳已取消`));
    xhr.send(file);
  });

  return { fileUrl, objectKey };
}
